import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { checkForMissingSlots, debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { ThemeConfig } from '@nylas/core';

/**
 * The `nylas-custom-booking-flow` component is an input form for booking type (automatically or manually accept bookings) and confirmation redirect URL (default or custom).
 * This component is a container for the `nylas-booking-confirmation-type`, `nylas-booking-confirmation-redirect` components.
 *
 * ```html
 * <nylas-custom-booking-flow>
 *   <div slot="inputs">
 *     <nylas-booking-confirmation-type></nylas-booking-confirmation-type>
 *     <nylas-booking-confirmation-redirect></nylas-booking-confirmation-redirect>
 *   </div>
 * </nylas-custom-booking-flow>
 * ```
 *
 * @slot inputs - The custom booking flow inputs. This includes the booking type, confirmation redirect type, and custom redirect URL components.
 */
@Component({
  tag: 'nylas-custom-booking-flow',
  styleUrl: 'nylas-custom-booking-flow.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasCustomBookingFlow {
  @Element() el!: HTMLElement;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-custom-booking-flow', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }
  disconnectedCallback() {
    debug('nylas-custom-booking-flow', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-custom-booking-flow', 'componentWillLoad');
  }

  componentDidLoad() {
    debug('nylas-custom-booking-flow', 'componentDidLoad');
    checkForMissingSlots(['inputs'], this.el);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasCustomBookingFlow, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-custom-booking-flow',
    eventToProps: {},
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host part="ncbflow">
        <nylas-form-card exportparts="nfc__header: ncbflow__header">
          <h1 slot="header-title" class="nylas-custom-booking-flow__title" part="ncbflow__title">
            {i18next.t('nylasCustomBookingFlow.headerTitle')}
          </h1>
          <div slot="content" class="nylas-custom-booking-flow__content" part="ncbflow__content">
            <slot name="inputs"></slot>
          </div>
        </nylas-form-card>
      </Host>
    );
  }
}
